import { catalogProducts, type CatalogProduct } from "./accessoryQuoteCatalog";
import { accessoryComparisonSections } from "./accessoryEquivalencies";

export type HardwareCategory = {
  slug: string;
  name: string;
  summary: string;
  planningChecks: string[];
};

export type HardwareProduct = CatalogProduct & {
  categorySlug: string;
  productSlug: string;
  path: string;
  referencePriceLabel: string;
};

export type HardwareTechnicalReference = {
  productType: string;
  details: string;
  qualification?: string;
  source: "comparison" | "catalog";
};

export type SubAssemblyGuide = {
  slug: string;
  name: string;
  categorySlug: string;
  summary: string;
  checks: string[];
  comparisonNote: string;
  options: Array<{ brand: string; model: string; product: string; details: string; qualification?: string }>;
};

const categoryDefinitions: Record<string, Omit<HardwareCategory, "slug">> = {
  Mirrors: {
    name: "Commercial Restroom Mirrors",
    summary: "Channel-frame, angle-frame, LED, and specialty mirror model references for commercial restroom planning.",
    planningChecks: ["Mirror width and height", "Frame style and finish", "Mounting or wall condition", "Lighting or electrical requirements"],
  },
  "Grab Bars": {
    name: "Commercial Grab Bars",
    summary: "Commercial grab-bar model references for project planning; verify length, diameter, backing, finish, and accessibility requirements.",
    planningChecks: ["Diameter and length", "Wall backing and substrate", "Finish and grip", "Project accessibility documentation"],
  },
  "Compartment Accessories": {
    name: "Compartment Accessories",
    summary: "Model references for tissue dispensers, seat-cover dispensers, sanitary units, and other compartment accessories.",
    planningChecks: ["Partition or wall mounting", "Projection and clearance", "Service access", "Finish and matching accessories"],
  },
  "Shower & Bath": {
    name: "Shower and Bath Accessories",
    summary: "Shower-rod and bath-accessory model references for commercial restroom and locker-room planning.",
    planningChecks: ["Mounting and substrate", "Length or configuration", "Finish", "Wet-area conditions"],
  },
  "Paper & Waste": {
    name: "Paper and Waste Accessories",
    summary: "Model references for towel dispensers, combination units, and waste receptacles.",
    planningChecks: ["Towel format and capacity", "Waste capacity", "Recessed or surface mounting", "Service access and wall depth"],
  },
  "Hooks & Storage": {
    name: "Hooks and Storage",
    summary: "Commercial robe-hook and storage model references for restroom planning, mounting conditions, finish coordination, and expected use.",
    planningChecks: ["Projection", "Mounting method", "Wall substrate", "Finish and expected use"],
  },
  "Hygiene & Soap": {
    name: "Hygiene and Soap Accessories",
    summary: "Commercial soap-dispenser reference models for restroom planning, refill access, mounting conditions, finish coordination, and service needs.",
    planningChecks: ["Liquid compatibility", "Refill access", "Orientation", "Mounting and projection"],
  },
};

export function slugifyHardware(value: string) {
  return value
    .toLowerCase()
    .replace(/&/g, " and ")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

const categorySlugByName = Object.fromEntries(Object.keys(categoryDefinitions).map((name) => [name, slugifyHardware(name)]));

export const hardwareCategories: HardwareCategory[] = Object.entries(categoryDefinitions).map(([name, definition]) => ({
  slug: categorySlugByName[name]!,
  ...definition,
}));

export const hardwareProducts: HardwareProduct[] = catalogProducts.map((product) => {
  const categorySlug = categorySlugByName[product.category] ?? slugifyHardware(product.category);
  const productSlug = `${slugifyHardware(product.model)}-${product.source.row}`;
  return {
    ...product,
    categorySlug,
    productSlug,
    path: `/hardware/${categorySlug}/${productSlug}`,
    referencePriceLabel: `Owner-supplied reference price: $${product.price.toFixed(2)}`,
  };
});

/**
 * The first set is intentionally small and uses specific, owner-supplied catalog
 * rows that also appear in the documented comparison boards. This avoids a thin,
 * mass-generated index of every catalog row before every model has a complete
 * project-use context.
 */
const featuredProductIds = new Set([
  "bobrick-b-165 2436-162",
  "asi-asi 0620-2436-82",
  "bobrick-b-6806.99x36-26",
  "asi-asi 3701-36-60",
  "bobrick-b-36903-51",
  "asi-asi 0469-69",
  "bobrick-b-47069c-82",
  "asi-asi 0864-f-73",
  "asi-asi 0032-75",
  "bobrick-b-221-153",
  "asi-asi 0477-sm-76",
  "bobrick-b-2111-155",
  "asi-asi 0347-77",
  "asi-asi 1214-3",
  "asi-asi 1214-2-60-4",
  "asi-asi 0751-78",
  "bobrick-b-6717-156",
]);

export const featuredHardwareProducts = hardwareProducts.filter((product) => featuredProductIds.has(product.id));

export function isFeaturedHardwareProduct(product: HardwareProduct) {
  return featuredProductIds.has(product.id);
}

export const subAssemblyGuides: SubAssemblyGuide[] = accessoryComparisonSections.flatMap((section) =>
  section.groups.map((group) => ({
    slug: group.id,
    name: group.title,
    categorySlug: section.routeGroupId,
    summary: group.comparisonNote,
    comparisonNote: group.comparisonNote,
    checks: [
      "Confirm the exact model and configuration.",
      "Verify dimensions, mounting, finish, and project requirements.",
      "Use the model references to prepare a project-specific quote request.",
    ],
    options: group.options,
  })),
);

function normalizeModelReference(value: string) {
  return value.toLowerCase().replace(/[^a-z0-9]/g, "");
}

function hasMatchingModelReference(product: HardwareProduct, optionModel: string) {
  const option = normalizeModelReference(optionModel);
  const productModel = normalizeModelReference(product.model);
  const family = normalizeModelReference(product.family);

  return [productModel, family].some((candidate) =>
    candidate === option ||
    (candidate.length >= 4 && option.length >= 4 && (candidate.includes(option) || option.includes(candidate))),
  );
}

/**
 * Returns the most specific technical summary already present in the approved
 * Bobrick/ASI/Bradley comparison workbook. Catalog-only rows retain their own
 * owner-supplied product description rather than inventing missing attributes.
 */
export function getHardwareTechnicalReference(product: HardwareProduct): HardwareTechnicalReference {
  const comparisonOption = isFeaturedHardwareProduct(product)
    ? accessoryComparisonSections
      .flatMap((section) => section.groups.flatMap((group) => group.options))
      .find((option) => option.brand === product.brand && hasMatchingModelReference(product, option.model))
    : undefined;

  if (comparisonOption) {
    return {
      productType: comparisonOption.product,
      details: comparisonOption.details,
      qualification: comparisonOption.qualification,
      source: "comparison",
    };
  }

  return {
    productType: product.category,
    details: product.description,
    source: "catalog",
  };
}

export function getHardwareCategory(slug: string | undefined) {
  return hardwareCategories.find((category) => category.slug === slug);
}

export function getHardwareProductsForCategory(categorySlug: string | undefined) {
  return hardwareProducts.filter((product) => product.categorySlug === categorySlug);
}

export function getFeaturedHardwareProductsForCategory(categorySlug: string | undefined) {
  return featuredHardwareProducts.filter((product) => product.categorySlug === categorySlug);
}

export function getHardwareProduct(categorySlug: string | undefined, productSlug: string | undefined) {
  return hardwareProducts.find((product) => product.categorySlug === categorySlug && product.productSlug === productSlug);
}

export function getSubAssemblyGuide(slug: string | undefined) {
  return subAssemblyGuides.find((guide) => guide.slug === slug);
}

export function findHardwareProductForModel(model: string) {
  const normalized = model.toLowerCase().replace(/[^a-z0-9]/g, "");
  return hardwareProducts.find((product) => product.model.toLowerCase().replace(/[^a-z0-9]/g, "") === normalized);
}
